import {addPropertiesToGridColumns} from "./gridColumnUtils";
import {GRID_COLUMN_PROPERTY} from "./Constants";

export function createExcelExportParams (sheetName, columnNames) {
    if (typeof sheetName === 'object' || sheetName == null || sheetName === '') {
        sheetName = 'Customer Portal'; //Max is 31 chars
    }
    return {sheetName: sheetName, columnKeys: columnNames};
}

/**
 * Several grids append a blank "filler" column ({headerName: '', field: '', type: 'flexColType'})
 * whose only job is to absorb the leftover horizontal space so the grid fills its container.
 * It carries no data and must never reach a CSV/Excel export, otherwise every exported row ends
 * with a stray empty column.
 */
const isSpacerColumn = (column) => {
    const colDef = column?.getColDef?.() || {};
    const type = Array.isArray(colDef.type) ? colDef.type : [colDef.type];
    const hasNoField = !colDef.field && !colDef.valueGetter;
    const hasNoHeader = !colDef.headerName;
    return type.includes('flexColType') || (hasNoField && hasNoHeader);
}

export const EXCEL_MAX_SHEET_NAME_LENGTH = 31;
/**
 * Reproduces the worksheet name Excel gives a file when it is opened directly (which is what the
 * user sees after downloading a report): the file's base name, with characters Excel forbids in a
 * sheet name swapped out, truncated to 31 chars.
 *
 * e.g. 'AccruedCash-FNYI_AGT-FNPF_USA-MultiCcy-20260925.csv' -> 'AccruedCash-FNYI_AGT-FNPF_USA-M'
 *
 * Falls back to 'Sheet1' when no usable file name is supplied, matching Excel's default.
 */
export const deriveSheetNameFromFileName = (fileName) => {
    if (typeof fileName !== 'string' || fileName.trim() === '') {
        return 'Sheet1';
    }

    // drop any directory prefix and the extension
    const baseName = fileName.split(/[\\/]/).pop().replace(/\.[^.]+$/, '');

    // Excel disallows : \ / ? * [ ] in sheet names.
    // Trim before truncating so leading blanks don't eat into the 31-char budget, and again
    // afterwards because the cut can land just past a space and leave a trailing blank.
    const sanitized = baseName.replace(/[:\\/?*[\]]/g, '_').trim()
        .substring(0, EXCEL_MAX_SHEET_NAME_LENGTH).trim();

    return sanitized === '' ? 'Sheet1' : sanitized;
}

export const getFilteredColumns = (grid) => {
    let filteredColumns = (grid && grid.current.columnApi )?
        [...grid.current.columnApi.getAllDisplayedColumns()]: [];
    return filteredColumns.length === 0 ? [] : filteredColumns
        .filter(item => !isSpacerColumn(item))
        .map(item => {
            return item.getColId()
        });
}


export const updateColumnHideState = (columnDefs, columnState)=> {
    let updatedColumnDefs;
    if(columnDefs !== null && columnDefs !== undefined && columnDefs?.length > 0)
        updatedColumnDefs = JSON.parse(JSON.stringify(columnDefs));

    if (columnState?.columnState &&
        Array.isArray(columnState?.columnState) &&
        columnState?.columnState?.length > 0) {
        updatedColumnDefs?.forEach((colDef)=>{
            const match = columnState.columnState.find(state => state.colId === colDef.field)
            if(match) {
                colDef.hide = match.hide
                colDef.sort = match.sort || null
            }
        })

    } else if(Array.isArray(columnState?.colIds)  && columnState?.colIds?.length > 0 ) {
        updatedColumnDefs = updateColumnIds(columnState, updatedColumnDefs)
    }

    else {
        updatedColumnDefs.forEach((colDef)=> {
            colDef.hide = colDef.field === 'tradeCancelIndicator' ||
                colDef.field === 'cdBlockIdClient' || colDef.field === 'version' || colDef.field === 'migrated';
            colDef.sort = null;
        })
    }
    updatedColumnDefs = addPropertiesToGridColumns(updatedColumnDefs, GRID_COLUMN_PROPERTY)
    return updatedColumnDefs;
}

export const updateColumnIds = (columnState, columnDefs) =>{
    const updateColumns = JSON.parse(JSON.stringify(columnDefs));
    if(Array.isArray(columnState?.colIds)  && columnState?.colIds?.length > 0 ) {
        updateColumns?.forEach((colDef)=>{
            colDef.hide = !columnState.colIds.find(col => col === colDef.field)
        })
    }
    return updateColumns;
}

/**
 * Safely turns a persisted gridState value into the { columnState, colIds } object that
 * updateColumnHideState expects.
 *
 * Returns null when there is nothing usable to apply - saved searches created before grid state
 * was persisted have a null/empty value, and callers must fall back to the default column defs
 * rather than passing an empty object into updateColumnHideState (its else-branch applies
 * trade-specific hide rules).
 */
export const parseGridState = (gridState) => {
    if (!gridState) {
        return null
    }

    let parsed = gridState
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed)
        } catch (e) {
            console.error('Unable to parse saved grid state: ', e)
            return null
        }
    }

    if (!parsed || typeof parsed !== 'object') {
        return null
    }

    const hasColumnState = Array.isArray(parsed.columnState) && parsed.columnState.length > 0
    const hasColIds = Array.isArray(parsed.colIds) && parsed.colIds.length > 0

    return (hasColumnState || hasColIds) ? parsed : null
}

/**
 * Applies a saved gridState onto column defs, returning new defs with the persisted hide/sort flags.
 *
 * Unlike updateColumnHideState this shallow-copies each column def instead of round-tripping it
 * through JSON, so valueGetter / valueFormatter / cellStyle functions survive. That matters for the
 * export grid - the wires defs use a valueGetter for Date and a valueFormatter for Outgoing Funds,
 * and losing them would export raw unformatted values.
 *
 * Returns the defs unchanged (with grid properties applied) when there is no usable saved state.
 */
export const applyGridStateToColumnDefs = (columnDefs, gridState) => {
    if (!Array.isArray(columnDefs) || columnDefs.length === 0) {
        return columnDefs
    }

    const state = parseGridState(gridState)
    if (!state) {
        return addPropertiesToGridColumns(columnDefs, GRID_COLUMN_PROPERTY)
    }

    const stateByColId = new Map()
    if (Array.isArray(state.columnState)) {
        state.columnState.forEach((entry) => {
            if (entry && entry.colId) {
                stateByColId.set(entry.colId, entry)
            }
        })
    }

    const displayedColIds = (Array.isArray(state.colIds) && state.colIds.length > 0)
        ? new Set(state.colIds)
        : null

    const updated = columnDefs.map((colDef) => {
        const colId = colDef.colId || colDef.field
        const match = colId ? stateByColId.get(colId) : undefined

        if (match) {
            return {...colDef, hide: !!match.hide, sort: match.sort || null}
        }
        if (displayedColIds && colId) {
            return {...colDef, hide: !displayedColIds.has(colId)}
        }
        return {...colDef}
    })

    return addPropertiesToGridColumns(updated, GRID_COLUMN_PROPERTY)
}

